// SPDX-License-Identifier: Elastic-2.0
/**
 * GradientOrb — three-layer radial-gradient atmosphere.
 *
 * Sits behind page content as decorative chrome. Used full-bleed by
 * onboarding (per `WizardShell.OSBg`) and as a subtler back-layer by
 * the dashboard (per technical spec § 7.5).
 *
 * Theme awareness lives entirely in the CSS module — this component
 * just renders three positioned divs and lets `:where([data-theme])`
 * selectors do the right thing. That keeps the React tree decoupled
 * from `useTheme` and means swapping the theme is a free re-render.
 *
 * Always `aria-hidden` + `pointer-events: none` + `z-index: 0`: page
 * content layered on top must use its own positive `z-index` /
 * `position: relative`.
 */
import styles from "./GradientOrb.module.css";

export type GradientOrbVariant = "full" | "subtle";

export interface GradientOrbProps {
  /**
   * Visual intensity. `"full"` is the onboarding hero look;
   * `"subtle"` dials all layer opacities down for dashboard back-chrome.
   */
  variant?: GradientOrbVariant;
}

export function GradientOrb({ variant = "full" }: GradientOrbProps): JSX.Element {
  const rootClass = variant === "subtle" ? `${styles.root} ${styles.subtle}` : styles.root;
  return (
    <div aria-hidden="true" className={rootClass} data-testid="gradient-orb">
      <div className={styles.layerCore} data-testid="gradient-orb-core" />
      <div className={styles.layerBloom} data-testid="gradient-orb-bloom" />
      <div className={styles.layerAccent} data-testid="gradient-orb-accent" />
    </div>
  );
}
